import { useEffect, useState } from 'react';
import { api, usd } from '../lib/api';
import { printReceipt } from '../lib/receipt';
import { ErrorNote, Modal } from './ui';

export interface Tender { method: 'cash' | 'card' | 'house_account'; amountCents: number }
const money = (v: string) => v.replace(/[^\d.]/g, '');
const centsOf = (v: string) => Math.round(Number(v || '0') * 100);

/** Pay one sale with several methods: part cash, part card, part charged to the customer's house account. */
export function SplitModal({ due, canCharge, customerName, onClose, onConfirm }: { due: number; canCharge: boolean; customerName?: string; onClose: () => void; onConfirm: (t: Tender[]) => void }) {
  const [rows, setRows] = useState<{ method: Tender['method']; amount: string }[]>([{ method: 'cash', amount: '' }, { method: 'card', amount: '' }]);
  const paid = rows.reduce((s, r) => s + centsOf(r.amount), 0);
  const cash = rows.filter((r) => r.method === 'cash').reduce((s, r) => s + centsOf(r.amount), 0);
  const nonCash = paid - cash;
  const remaining = due - paid;
  const change = Math.max(0, paid - due);
  const ok = paid >= due && change <= cash && nonCash <= due && paid > 0;
  const fill = (i: number) => setRows(rows.map((r, j) => (j === i ? { ...r, amount: (Math.max(0, remaining + centsOf(r.amount)) / 100).toFixed(2) } : r)));
  return (
    <Modal title="Split payment" onClose={onClose} footer={<button className="primary big" disabled={!ok} onClick={() => onConfirm(rows.filter((r) => centsOf(r.amount) > 0).map((r) => ({ method: r.method, amountCents: centsOf(r.amount) })))}>Take payment</button>}>
      <div className="totals" style={{ padding: 0 }}>
        <div className="line"><span>To pay</span><span>{usd(due)}</span></div>
        <div className="line"><span>Entered</span><span>{usd(paid)}</span></div>
        {remaining > 0 ? <div className="grand"><span>Still to pay</span><span>{usd(remaining)}</span></div> : <div className="grand"><span>{change > 0 ? 'Change due' : 'Covered'}</span><span>{usd(change)}</span></div>}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="field-row">
          <label>Method<select value={r.method} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, method: e.target.value as Tender['method'] } : x)))}>
            <option value="cash">Cash</option><option value="card">Card</option>{canCharge && <option value="house_account">Charge to {customerName ?? 'account'}</option>}
          </select></label>
          <label>Amount<input className="num-input" inputMode="decimal" value={r.amount} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: money(e.target.value) } : x)))} placeholder="0.00" /></label>
          <button onClick={() => fill(i)}>Rest</button>
          {rows.length > 1 && <button className="ghost danger" aria-label="Remove" onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</button>}
        </div>))}
      <button onClick={() => setRows([...rows, { method: 'card', amount: '' }])}>Add another payment</button>
      {change > cash && <div className="notice bad">Change can only be given from cash.</div>}
      {nonCash > due && <div className="notice bad">Card and account amounts cannot be more than what is owed.</div>}
    </Modal>
  );
}

/** Type in a coupon code; the register checks it with the server as part of totalling the sale. */
export function CouponPrompt({ onClose, onAdd }: { onClose: () => void; onAdd: (code: string) => void }) {
  const [code, setCode] = useState('');
  return (
    <Modal title="Coupon" onClose={onClose}>
      <form className="scanbar" onSubmit={(e) => { e.preventDefault(); if (code.trim()) onAdd(code.trim().toUpperCase()); }}>
        <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan or type the coupon code" aria-label="Coupon code" />
        <button className="primary big">Apply</button>
      </form>
    </Modal>
  );
}

/** Special orders that have arrived: pick one to ring up, with its deposit applied. */
export function PickupOrders({ onClose, onPick }: { onClose: () => void; onPick: (order: any) => void }) {
  const [list, setList] = useState<any[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { api<any[]>('/api/special-orders?status=received').then(setList).catch(setErr); }, []);
  async function pick(id: string) { setErr(null); try { onPick(await api(`/api/special-orders/${id}`)); } catch (e) { setErr(e); } }
  return (
    <Modal title="Special orders ready for pickup" onClose={onClose}>
      <ErrorNote error={err} />
      {list && list.length === 0 && <div className="empty">Nothing is waiting. Orders appear here once they are marked received.</div>}
      {list?.map((o) => (
        <button key={o.id} className="ghost" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }} onClick={() => pick(o.id)}>
          <span>{o.customer_name ?? o.contact_name} · {o.line_count} item{o.line_count === 1 ? '' : 's'}</span><b>{usd(o.total_cents)}{o.deposit_cents - o.deposit_applied_cents > 0 ? ` · ${usd(o.deposit_cents - o.deposit_applied_cents)} deposit` : ''}</b>
        </button>))}
    </Modal>
  );
}

/** Reprint a receipt from a recent sale. */
export function RecentReceipts({ onClose }: { onClose: () => void }) {
  const [list, setList] = useState<any[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { api<any[]>('/api/sales').then((r) => setList(r.slice(0, 25))).catch(setErr); }, []);
  return (
    <Modal title="Reprint a receipt" onClose={onClose}>
      <ErrorNote error={err} />
      {list?.map((s) => (
        <button key={s.id} className="ghost" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }} onClick={async () => { if (!(await printReceipt(s.id))) setErr(new Error('The browser blocked the print window. Allow pop-ups for this site.')); }}>
          <span>#{String(s.id).slice(0, 8)} · {new Date(s.sold_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span><b>{usd(s.total_cents)}</b>
        </button>))}
      {list && list.length === 0 && <div className="empty">No sales yet.</div>}
    </Modal>
  );
}
